import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {User} from '../../../../../shared/models/user.types';
import {AuthService} from '../../../../../core/auth/auth.service';
import {ReviewInfo} from '../../models/review-info';
import {HomeService} from '../../services/home.service';
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexGrid,
    ApexLegend,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
    ChartComponent
} from 'ng-apexcharts';
import {ConsolidateReview} from '../../models/consolidate-review';
import {MySubscriptionInfo} from '../../models/my-subscription-info';
import {LocationService} from '../../../../../shared/services/location.service';
import {AllLocationResponse} from '../../../../../shared/models/location.types';
import {Router} from '@angular/router';
import {MyOrgSubscription} from '../../models/my-org-subscription';
import {DateTime, Duration} from 'luxon';
import {PropertyGroup} from '../../../../../shared/constants/property-group';
import {SettingService} from '../../../collect-review/services/setting.service';
import {ShortcutsService} from '../../../../../layout/common/shortcuts/shortcuts.service';
import {Device} from '@capacitor/device';
import {TranslocoService} from '@ngneat/transloco';
import {QuickSetupService} from '../../services/quick-setup.service';
import {LTDService} from '../../../agency-mode/services/ltd.service';
import {WhiteLabelPropertyService} from '../../../agency-mode/services/white-label-property.service';
import {SurveyCategoryCount} from '../../models/survey-category-count';
import {SetupType} from '../../../../../shared/constants/setup-type';
import {scalePx} from '../../../../../shared/constants/app-density';
import {AnalyticsService} from '../../../analytics/services/analytics.service';
import {AdvanceAnalyticsService} from '../../../analytics/services/advance-analytics.service';
import {ReviewResponse} from '../../../analytics/models/review-response';
import {SequenceStatusMetric} from '../../../analytics/models/sequence-status-metric';
import {DetailedReviewData, ReviewDetailData} from '../../../analytics/models/review-detail-data';
import {GoogleScraperService} from '../../../../../shared/services/google-scraper.service';
import {BusinessInfo} from '../../../../../shared/models/business-info';
import {ReputationAlertsService} from '../../services/reputation-alerts.service';
import {
    CampaignChartTotals,
    CampaignPerformanceFilter,
    ChartGranularity,
    KpiCardMeta,
    RecentFeedbackRow,
    ReputationAlert,
    ReputationDateFilter,
    ReputationKpis
} from '../../models/reputation-dashboard.models';
import {DateToUtc} from '../../../../../shared/functions/date-to-utc';
import {FormControl, FormGroup} from '@angular/forms';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {LuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS} from '@angular/material-luxon-adapter';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

declare var crispChat: any;

export type ReputationChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    colors: string[];
    tooltip: ApexTooltip;
    plotOptions: ApexPlotOptions;
    legend: ApexLegend;
    grid: ApexGrid;
};

@Component({
    selector: 'home',
    templateUrl: './home.component.html',
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS],
        },
        {
            provide: MAT_DATE_FORMATS, useValue: {
                display: {
                    dateInput: 'dd MMM yyyy',
                    monthYearLabel: 'MMM yyyy',
                    dateA11yLabel: 'LL',
                    monthYearA11yLabel: 'MMMM yyyy',
                }
            }
        },
    ]
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('chart') chart: ChartComponent;
    public chartOptions: Partial<ReputationChartOptions>;
    currentUser: User;
    reviewInfo: ReviewInfo;
    consolidateReviews: ConsolidateReview;
    reviewResponse: ReviewResponse;
    businessInformation: BusinessInfo;
    mySubscriptionInfo: MySubscriptionInfo;
    currentLocation: AllLocationResponse;
    orgSubscription: MyOrgSubscription;
    isLoading = true;
    isRefreshing = false;
    isMobileDevice = false;
    setupDelayInMinutes = 0;
    setupProgressStatus = 1;
    validSubscriptionForAnalytics = false;
    npsSurveyCategory: SurveyCategoryCount;
    csatSurveyCategory: SurveyCategoryCount;
    dateFilter: ReputationDateFilter = 'last-10-days';
    campaignFilter: CampaignPerformanceFilter = 'all';
    chartGranularity: ChartGranularity = 'daily';
    readonly kpiCards: KpiCardMeta[] = [
        {
            title: 'Campaign Enrollments',
            tooltip: 'Customers added to review campaigns',
            icon: 'heroicons_outline:user-group',
            iconClass: 'bg-indigo-50 text-indigo-600'
        },
        {
            title: 'Active Enrollments',
            tooltip: 'Customers currently in an active sequence',
            icon: 'heroicons_outline:lightning-bolt',
            iconClass: 'bg-blue-50 text-blue-600'
        },
        {
            title: 'Responses Received',
            tooltip: 'Customers who provided feedback',
            icon: 'heroicons_outline:chat-alt-2',
            iconClass: 'bg-green-50 text-green-600'
        },
        {
            title: 'Google Reviews',
            tooltip: 'Public reviews on Google',
            icon: 'mat_outline:stars',
            iconClass: 'bg-amber-50 text-amber-600'
        },
        {
            title: 'Google Rating',
            tooltip: 'Current public Google rating from live business information.',
            icon: 'heroicons_outline:star',
            iconClass: 'bg-yellow-50 text-yellow-600'
        }
    ];
    range = new FormGroup({
        startDate: new FormControl<DateTime | null>(null),
        endDate: new FormControl<DateTime | null>(null),
    });
    kpis: ReputationKpis = this.emptyKpis();
    chartTotals: CampaignChartTotals = this.emptyChartTotals();
    recentFeedback: RecentFeedbackRow[] = [];
    alerts: ReputationAlert[] = [];
    recentFeedbackColumns = ['customer', 'sequence', 'rating', 'status', 'time'];
    fromDateLabel = '';
    toDateLabel = '';
    googleReviewsThisMonth = 0;
    totalGoogleReviews = 0;
    private chartMetrics: SequenceStatusMetric[] = [];
    private destroy$ = new Subject<void>();

    constructor(private authService: AuthService,
                private homeService: HomeService,
                private locationService: LocationService,
                private router: Router,
                private settingService: SettingService,
                private shortcutService: ShortcutsService,
                private translocoService: TranslocoService,
                private quickSetupService: QuickSetupService,
                private ltdService: LTDService,
                private whiteLabelPropertyService: WhiteLabelPropertyService,
                private analyticsService: AnalyticsService,
                private advanceAnalyticsService: AdvanceAnalyticsService,
                private googleScraperService: GoogleScraperService,
                private reputationAlertsService: ReputationAlertsService
    ) {
    }

    ngOnInit() {
        if (!this.router.url.includes('dashboard')) {
            this.router.navigate(['dashboard']);
        }
        this.initChartOptions();
        this.currentUser = this.authService.businessUser;
        this.homeService.currentOrgSubscriptionInfo.pipe(takeUntil(this.destroy$)).subscribe(info => this.orgSubscription = info);
        this.homeService.currentNativeDeviceInfo.pipe(takeUntil(this.destroy$)).subscribe(isNative => this.isMobileDevice = isNative);
        this.locationService.currentLocation.pipe(takeUntil(this.destroy$)).subscribe(location => {
            if (location?.locationId) {
                this.currentLocation = location;
                crispChat(this.currentUser.emailAddress, location.name, this.currentUser.fullName);
                this.setupDelayInMinutes = this.locationService.getSetupDelay();
                this.loadDashboardData();
                this.setSetupProgress();
            } else if (!this.locationService.currentLocationDetail?.locationId) {
                this.router.navigate(['/dashboard/select-location']);
            } else {
                this.currentLocation = this.locationService.currentLocationDetail;
                this.setupDelayInMinutes = this.locationService.getSetupDelay();
                this.loadDashboardData();
                this.setSetupProgress();
            }
        });
        this.range.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(val => {
            if (val.startDate && val.endDate && this.dateFilter === 'custom') {
                this.loadDashboardData();
            }
        });
        localStorage.removeItem('dateFilterFromDashboard');
        this.getPreferredSequence();
    }

    ngAfterViewInit(): void {
        if (this.chartMetrics.length) {
            this.applyCampaignChart(this.chartMetrics);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onDateFilterChange(): void {
        if (this.dateFilter !== 'custom') {
            this.loadDashboardData();
        }
    }

    loadDashboardData(): void {
        if (!this.currentLocation?.locationId) {
            return;
        }
        const {startDate, endDate} = this.resolveDateRange();
        this.fromDateLabel = startDate.toFormat('dd MMM yyyy');
        this.toDateLabel = endDate.toFormat('dd MMM yyyy');
        this.isRefreshing = !this.isLoading;
        if (!this.isRefreshing) {
            this.isLoading = true;
        }
        const locationId = this.currentLocation.locationId;

        this.homeService.getConsolidateReviews2(locationId).subscribe({
            next: (res) => {
                this.consolidateReviews = res;
                localStorage.setItem('customerEnrolled', res.totalInvites.toString());
                this.checkCustomerEnroll();
            },
            error: (error) => console.error(error.error)
        });

        this.homeService.getMySubscriptionInfo(locationId).subscribe({
            next: (res) => {
                this.mySubscriptionInfo = res;
                this.homeService.locationSubscription = res;
                this.validSubscriptionForAnalytics = DateTime.fromISO(res.analyticsCutoffTime).diff(DateTime.now(), 'days').days > 0;
            },
            error: () => {
                this.mySubscriptionInfo = null;
                this.homeService.locationSubscription = null;
            }
        });

        if (this.setupDelayInMinutes > 7) {
            this.homeService.getReviewInformation(locationId).subscribe({
                next: (res) => this.reviewInfo = res,
                error: (error) => console.error(error.error)
            });
            ['NPS', 'CSAT'].forEach(category => {
                this.homeService.getQuestionCount(locationId, category).subscribe({
                    next: (res) => {
                        if (category === 'NPS') {
                            this.npsSurveyCategory = res;
                        } else {
                            this.csatSurveyCategory = res;
                        }
                    },
                    error: (error) => console.error(error.error)
                });
            });
        }

        this.googleScraperService.getBusinessInformation(locationId).subscribe({
            next: (res) => {
                this.businessInformation = res;
                this.totalGoogleReviews = res.ratingInfo?.currentReviewCount ?? 0;
                if (this.reviewResponse) {
                    this.buildKpis(this.reviewResponse);
                }
            },
            error: () => this.businessInformation = null
        });

        this.analyticsService.getReviewData(locationId, DateToUtc(startDate), DateToUtc(endDate, false)).subscribe({
            next: (res) => {
                this.reviewResponse = res;
                this.buildKpis(res);
                this.loadAlerts(res.inProgress ?? 0);
            },
            error: (error) => {
                console.error(error);
                this.buildKpis(null);
                this.loadAlerts(0);
            }
        });

        this.advanceAnalyticsService.getSequenceStatusMetric(startDate.toISODate(), endDate.toISODate(), locationId).subscribe({
            next: (res) => {
                this.chartMetrics = this.fillMetricGaps(res, startDate, endDate);
                this.applyCampaignChart(this.chartMetrics);
            },
            error: (error) => console.error(error)
        });

        this.analyticsService.getDetailedData(locationId, 0, 'All', DateToUtc(startDate), DateToUtc(endDate, false)).subscribe({
            next: (res) => {
                this.recentFeedback = this.mapRecentFeedback(res);
                this.isLoading = false;
                this.isRefreshing = false;
            },
            error: (error) => {
                console.error(error);
                this.recentFeedback = [];
                this.isLoading = false;
                this.isRefreshing = false;
            }
        });
    }

    get dateFilterLabel(): string {
        const labels: Record<ReputationDateFilter, string> = {
            'last-10-days': 'Last 10 Days',
            'last-30-days': 'Last 30 Days',
            'this-month': 'This Month',
            'last-month': 'Last Month',
            'custom': 'Custom Range'
        };
        return labels[this.dateFilter];
    }

    get campaignFilterLabel(): string {
        const labels: Record<CampaignPerformanceFilter, string> = {
            all: 'All Campaigns',
            'review-campaigns': 'Review Campaigns',
            'google-reviews': 'Google Reviews',
            'feedback-responses': 'Feedback Responses'
        };
        return labels[this.campaignFilter];
    }

    get chartGranularityLabel(): string {
        const labels: Record<ChartGranularity, string> = {
            daily: 'Daily',
            weekly: 'Weekly',
            monthly: 'Monthly'
        };
        return labels[this.chartGranularity];
    }

    setCampaignFilter(filter: CampaignPerformanceFilter): void {
        this.campaignFilter = filter;
        this.applyCampaignChart(this.chartMetrics);
    }

    clearCampaignFilter(event: MouseEvent): void {
        event.stopPropagation();
        event.preventDefault();
        this.setCampaignFilter('all');
    }

    setChartGranularity(granularity: ChartGranularity): void {
        this.chartGranularity = granularity;
        this.applyCampaignChart(this.chartMetrics);
    }

    clearChartGranularity(event: MouseEvent): void {
        event.stopPropagation();
        event.preventDefault();
        this.setChartGranularity('daily');
    }

    setDateFilter(filter: ReputationDateFilter): void {
        this.dateFilter = filter;
        this.onDateFilterChange();
    }

    clearDateFilter(event: MouseEvent): void {
        event.stopPropagation();
        event.preventDefault();
        this.setDateFilter('last-10-days');
    }

    getKpiValue(index: number): number {
        switch (index) {
            case 0: return this.kpis.campaignEnrollments;
            case 1: return this.kpis.activeEnrollments;
            case 2: return this.kpis.responsesReceived;
            case 3: return this.kpis.googleReviews;
            case 4: return this.kpis.googleRating;
            default: return 0;
        }
    }

    getKpiDelta(index: number): string {
        switch (index) {
            case 0: return `+${this.kpis.enrollmentsDelta}`;
            case 1: return `+${this.kpis.activeDelta}`;
            case 2: return `+${this.kpis.responsesDelta}`;
            case 3: return `+${this.kpis.googleReviewsDelta}`;
            default: return '';
        }
    }

    getKpiDeltaLabel(index: number): string {
        return index === 1 ? 'this week' : 'this month';
    }

    showKpiRating(index: number): boolean {
        return index === 4 && !!this.kpis.googleRating;
    }

    showResponseRatePill(index: number): boolean {
        return index === 2;
    }

    showConversionRatePill(index: number): boolean {
        return index === 3;
    }

    customerInitials(name: string): string {
        const parts = (name || '').trim().split(/\s+/).filter(Boolean);
        if (!parts.length) {
            return '?';
        }
        if (parts.length === 1) {
            return parts[0].slice(0, 2).toUpperCase();
        }
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }

    alertTileClass(severity: ReputationAlert['severity']): string {
        if (severity === 'red') {
            return 'bg-red-50 text-red-600';
        }
        if (severity === 'orange') {
            return 'bg-amber-50 text-amber-600';
        }
        return 'bg-blue-50 text-blue-600';
    }

    private loadAlerts(inProgressCount: number): void {
        if (!this.currentLocation?.locationId) {
            return;
        }
        this.reputationAlertsService.getAlerts(this.currentLocation.locationId, inProgressCount).subscribe({
            next: (alerts) => this.alerts = alerts,
            error: () => this.alerts = []
        });
    }

    private buildKpis(reviewData: ReviewResponse | null): void {
        const enrollments = reviewData?.totalEnrolled ?? this.consolidateReviews?.invitesThisMonth ?? 0;
        const active = (reviewData?.inProgress ?? 0) + (reviewData?.scheduled ?? 0);
        const responses = (reviewData?.reviewed ?? 0) + (reviewData?.feedback ?? 0);
        const googleReviews = reviewData?.reviewCount?.['Google'] ?? reviewData?.reviewed ?? 0;
        const googleRating = this.businessInformation?.ratingInfo?.currentAvgRating
            ?? this.reviewInfo?.reviewInfoDetailList?.find(site => site.siteName === 'Google')?.rating
            ?? 0;
        this.googleReviewsThisMonth = googleReviews;
        const responseRate = enrollments ? Math.round((responses / enrollments) * 100) : 0;
        const conversionRate = enrollments ? Math.round((googleReviews / enrollments) * 100) : 0;

        this.kpis = {
            campaignEnrollments: enrollments,
            activeEnrollments: active,
            responsesReceived: responses,
            googleReviews,
            googleRating,
            responseRate,
            conversionRate,
            enrollmentsDelta: this.consolidateReviews?.invitesThisMonth ?? enrollments,
            activeDelta: reviewData?.inProgress ?? 0,
            responsesDelta: responses,
            googleReviewsDelta: googleReviews,
            ratingDelta: 0
        };
        this.chartTotals = {
            enrollments,
            responses,
            googleReviews,
            responseRate,
            conversionRate
        };
    }

    private mapRecentFeedback(data: ReviewDetailData): RecentFeedbackRow[] {
        return (data?.detailedReviewData || []).slice(0, 8).map((row: DetailedReviewData) => {
            const customerName = [row.customerName, row.lastName].filter(Boolean).join(' ').trim() || '—';
            const {rating, ratingScale} = this.resolveFeedbackRating(row);
            return {
                customerName,
                initials: this.customerInitials(customerName),
                subtitle: row.sequenceName || row.reviewSiteName || '—',
                rating,
                ratingScale,
                status: row.status || '—',
                statusClass: this.statusClass(row.status),
                time: row.reviewDate || row.sequenceStageData?.[0]?.triggerDate || row.sequenceStageData?.[0]?.reviewDate || ''
            };
        });
    }

    private resolveFeedbackRating(row: DetailedReviewData): { rating: number | null; ratingScale: number } {
        if (row.rating != null && row.rating !== '') {
            const rating = Number(row.rating);
            if (!Number.isNaN(rating)) {
                return {rating, ratingScale: rating > 5 ? 10 : 5};
            }
        }

        const stage = this.respondedFeedbackStage(row);
        if (stage?.rating != null && stage.rating !== '') {
            const rating = Number(stage.rating);
            if (!Number.isNaN(rating)) {
                return {rating, ratingScale: rating > 5 ? 10 : 5};
            }
        }

        const ratingQuestion = stage?.questionResponses?.find(questionResponse =>
            this.isRatingQuestionType(questionResponse.questionType) && questionResponse.responseValue
        );
        if (ratingQuestion?.responseValue) {
            const rating = Number(ratingQuestion.responseValue);
            if (!Number.isNaN(rating)) {
                return {
                    rating,
                    ratingScale: this.ratingScaleForQuestionType(ratingQuestion.questionType)
                };
            }
        }

        return {rating: null, ratingScale: 5};
    }

    private respondedFeedbackStage(row: DetailedReviewData) {
        const stages = [...(row.sequenceStageData || [])].reverse();
        return stages.find(stage =>
            !!stage.questionResponses?.length ||
            !!stage.feedback?.trim() ||
            (stage.rating != null && stage.rating !== '')
        ) || null;
    }

    private isRatingQuestionType(questionType: string): boolean {
        return ['RATING_1_10', 'SMILEY_1_5', 'STAR_1_5'].includes(questionType);
    }

    private ratingScaleForQuestionType(questionType: string): number {
        return questionType === 'RATING_1_10' ? 10 : 5;
    }

    statusClass(status: string): string {
        const normalized = (status || '').toLowerCase();
        if (normalized.includes('attention') || normalized.includes('feedback') || normalized.includes('low')) {
            return 'bg-red-100 text-red-700';
        }
        if (normalized.includes('review') || normalized.includes('google')) {
            return 'bg-blue-100 text-blue-700';
        }
        if (normalized.includes('progress') || normalized.includes('ongoing') || normalized.includes('scheduled')) {
            return 'bg-amber-100 text-amber-700';
        }
        return 'bg-gray-100 text-gray-700';
    }

    ratingClass(rating: number | null, ratingScale = 5): string {
        if (rating == null) {
            return 'text-secondary';
        }
        const normalized = ratingScale === 10 ? rating / 2 : rating;
        if (normalized <= 2) {
            return 'text-red-600 font-semibold';
        }
        if (normalized >= 4) {
            return 'text-green-700 font-semibold';
        }
        return 'text-amber-600 font-semibold';
    }

    private resolveDateRange(): { startDate: DateTime; endDate: DateTime } {
        const today = DateTime.now();
        if (this.dateFilter === 'this-month') {
            return {startDate: today.startOf('month'), endDate: today.endOf('month')};
        }
        if (this.dateFilter === 'last-month') {
            const lastMonth = today.minus(Duration.fromObject({months: 1}));
            return {startDate: lastMonth.startOf('month'), endDate: lastMonth.endOf('month')};
        }
        if (this.dateFilter === 'last-30-days') {
            return {startDate: today.minus({days: 29}).startOf('day'), endDate: today.endOf('day')};
        }
        if (this.dateFilter === 'custom' && this.range.valid && this.range.value.startDate && this.range.value.endDate) {
            return {
                startDate: this.range.value.startDate,
                endDate: this.range.value.endDate
            };
        }
        return {startDate: today.minus({days: 9}).startOf('day'), endDate: today.endOf('day')};
    }

    private fillMetricGaps(metrics: SequenceStatusMetric[], startDate: DateTime, endDate: DateTime): SequenceStatusMetric[] {
        const filled: SequenceStatusMetric[] = [];
        let currentDate = startDate.startOf('day');
        while (currentDate <= endDate) {
            const existing = metrics.find(metric => DateTime.fromISO(metric.date).toISODate() === currentDate.toISODate());
            filled.push(existing || {
                date: currentDate.toISODate(),
                totalInvite: 0,
                reviewCount: 0,
                feedbackCount: 0,
                noOutCome: 0
            });
            currentDate = currentDate.plus({days: 1});
        }
        return filled;
    }

    private applyCampaignChart(metrics: SequenceStatusMetric[]): void {
        if (!metrics?.length) {
            return;
        }
        const aggregated = this.aggregateChartMetrics(metrics);
        const enrollments = aggregated.reduce((sum, item) => sum + item.totalInvite, 0);
        const responses = aggregated.reduce((sum, item) => sum + item.feedbackCount, 0);
        const googleReviews = aggregated.reduce((sum, item) => sum + item.reviewCount, 0);
        this.chartTotals = {
            enrollments,
            responses,
            googleReviews,
            responseRate: enrollments ? Math.round((responses / enrollments) * 100) : 0,
            conversionRate: enrollments ? Math.round((googleReviews / enrollments) * 100) : 0
        };
        const categories = aggregated.map(item => this.formatChartCategory(item.date));
        const allSeries = [
            {
                name: this.translocoService.translate('Enrollments'),
                data: aggregated.map(item => item.totalInvite),
                key: 'enrollments' as const
            },
            {
                name: this.translocoService.translate('Responses'),
                data: aggregated.map(item => item.feedbackCount),
                key: 'responses' as const
            },
            {
                name: this.translocoService.translate('Google Reviews'),
                data: aggregated.map(item => item.reviewCount),
                key: 'google-reviews' as const
            }
        ];
        const filteredSeries = this.filterChartSeries(allSeries);
        this.chartOptions = {
            ...this.chartOptions,
            xaxis: {
                ...this.chartOptions.xaxis,
                categories,
                labels: {
                    style: {fontSize: '10px'},
                    rotate: -45,
                    rotateAlways: categories.length > 12
                }
            },
            series: filteredSeries.map(({name, data}) => ({name, data}))
        };
    }

    private aggregateChartMetrics(metrics: SequenceStatusMetric[]): SequenceStatusMetric[] {
        if (this.chartGranularity === 'daily') {
            return metrics;
        }
        const grouped = new Map<string, SequenceStatusMetric>();
        metrics.forEach((item) => {
            const date = DateTime.fromISO(item.date);
            const bucketDate = this.chartGranularity === 'weekly'
                ? date.startOf('week').toISODate()
                : date.startOf('month').toISODate();
            const existing = grouped.get(bucketDate) ?? {
                date: bucketDate,
                totalInvite: 0,
                feedbackCount: 0,
                reviewCount: 0,
                noOutCome: 0
            };
            existing.totalInvite += item.totalInvite;
            existing.feedbackCount += item.feedbackCount;
            existing.reviewCount += item.reviewCount;
            existing.noOutCome += item.noOutCome;
            grouped.set(bucketDate, existing);
        });
        return Array.from(grouped.values()).sort((a, b) => a.date.localeCompare(b.date));
    }

    private formatChartCategory(date: string): string {
        const parsed = DateTime.fromISO(date);
        if (this.chartGranularity === 'monthly') {
            return parsed.toFormat('MMM yyyy');
        }
        return parsed.toFormat('dd MMM');
    }

    private filterChartSeries(series: Array<{name: string; data: number[]; key: 'enrollments' | 'responses' | 'google-reviews'}>): Array<{name: string; data: number[]}> {
        switch (this.campaignFilter) {
            case 'review-campaigns':
                return series.filter(item => item.key === 'enrollments' || item.key === 'responses');
            case 'google-reviews':
                return series.filter(item => item.key === 'google-reviews');
            case 'feedback-responses':
                return series.filter(item => item.key === 'responses');
            default:
                return series;
        }
    }

    private initChartOptions(): void {
        this.chartOptions = {
            colors: ['#6654e2', '#108112', '#f59e0b'],
            series: [
                {name: 'Enrollments', data: []},
                {name: 'Responses', data: []},
                {name: 'Google Reviews', data: []}
            ],
            chart: {
                height: scalePx(260),
                type: 'bar',
                toolbar: {show: false},
                parentHeightOffset: 0,
                fontFamily: 'inherit'
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '42%',
                    borderRadius: 4
                }
            },
            legend: {
                position: 'top',
                horizontalAlign: 'left',
                fontSize: '12px',
                markers: {radius: 12}
            },
            grid: {
                strokeDashArray: 4,
                borderColor: '#e5e7eb',
                padding: {left: 8, right: 8}
            },
            xaxis: {
                categories: [],
                axisBorder: {show: false},
                axisTicks: {show: false},
                labels: {style: {fontSize: '10px'}}
            },
            yaxis: {
                min: 0,
                forceNiceScale: true,
                labels: {
                    formatter: (val) => Math.round(val).toString(),
                    style: {fontSize: '11px'}
                }
            },
            tooltip: {
                followCursor: true,
                theme: 'dark'
            }
        };
    }

    private emptyKpis(): ReputationKpis {
        return {
            campaignEnrollments: 0,
            activeEnrollments: 0,
            responsesReceived: 0,
            googleReviews: 0,
            googleRating: 0,
            responseRate: 0,
            conversionRate: 0,
            enrollmentsDelta: 0,
            activeDelta: 0,
            responsesDelta: 0,
            googleReviewsDelta: 0,
            ratingDelta: 0
        };
    }

    private emptyChartTotals(): CampaignChartTotals {
        return {
            enrollments: 0,
            responses: 0,
            googleReviews: 0,
            responseRate: 0,
            conversionRate: 0
        };
    }

    setSetupProgress() {
        if (!this.currentLocation) {
            return;
        }
        this.settingService.getAllProperties(this.currentLocation.locationId, PropertyGroup.GENERAL).subscribe(properties => {
            const locationSetup = properties.filter(property => property.propertyKey === 'locationSetup' && property.propertyValue === 'true');
            if (locationSetup.length > 0) {
                this.setupProgressStatus = 1;
            }
            const sequenceSetup = properties.filter(property => property.propertyKey === 'sequenceSetup' && property.propertyValue === 'true');
            if (sequenceSetup.length > 0) {
                this.setupProgressStatus = 2;
            }
            const customerEnrolled = properties.filter(property => property.propertyKey === 'customerEnrolled' && property.propertyValue === 'true');
            if (customerEnrolled.length > 0) {
                this.setupProgressStatus = 3;
            }
            const finalizeSetupProgress = () => {
                this.shortcutService.currentSetupProgressDetail = this.setupProgressStatus;
            };
            if (this.setupProgressStatus < 2) {
                this.checkGlobalSequenceSetup(() => {
                    if (this.setupProgressStatus < 3) {
                        this.checkCustomerEnroll(finalizeSetupProgress);
                    } else {
                        finalizeSetupProgress();
                    }
                });
            } else if (this.setupProgressStatus < 3) {
                this.checkCustomerEnroll(finalizeSetupProgress);
            } else {
                finalizeSetupProgress();
            }
        });
    }

    checkGlobalSequenceSetup(onComplete: () => void): void {
        const userType = this.currentUser?.type;
        if (userType !== SetupType.GLOBAL && userType !== SetupType.SHARED) {
            onComplete();
            return;
        }
        this.homeService.getAllSequenceMaster().subscribe({
            next: (sequences) => {
                const hasActiveSequence = (sequences || []).some(sequence => sequence.active);
                if (hasActiveSequence) {
                    this.markSequenceSetup(onComplete);
                } else {
                    onComplete();
                }
            },
            error: () => onComplete()
        });
    }

    markSequenceSetup(onComplete?: () => void): void {
        if (!this.currentLocation?.locationId) {
            onComplete?.();
            return;
        }
        this.settingService.createOrUpdateProperties({
            groupName: PropertyGroup.GENERAL,
            locPropertyDetailList: [{
                propertyKey: 'sequenceSetup',
                propertyValue: true
            }],
            locationId: this.currentLocation.locationId,
            orgId: this.authService.businessUser.orgId
        }).subscribe({
            next: () => {
                this.setupProgressStatus = 2;
                onComplete?.();
            },
            error: () => onComplete?.()
        });
    }

    editLocation() {
        this.quickSetupService.reviewSiteMaster().subscribe({
            next: (res) => localStorage.setItem('review_site_master', JSON.stringify(res)),
            error: (error) => console.log(error)
        });
        this.quickSetupService.getReviewSiteByLocation(this.currentLocation.locationId).subscribe({
            next: (res) => {
                localStorage.setItem('review_site_location', JSON.stringify(res));
                this.router.navigate(['dashboard/modify-location']);
            },
            error: (error) => console.log(error)
        });
    }

    checkCustomerEnroll(onComplete?: () => void) {
        if (this.consolidateReviews?.totalInvites) {
            this.settingService.createOrUpdateProperties({
                groupName: PropertyGroup.GENERAL,
                locPropertyDetailList: [{
                    propertyKey: 'customerEnrolled',
                    propertyValue: true
                }],
                locationId: this.currentLocation.locationId,
                orgId: this.authService.businessUser.orgId
            }).subscribe({
                next: () => {
                    this.setupProgressStatus = 3;
                    onComplete?.();
                },
                error: () => onComplete?.()
            });
        } else {
            onComplete?.();
        }
    }

    goToReviewAnalytics(dateFilter = 'all_time') {
        localStorage.setItem('dateFilterFromDashboard', dateFilter);
        this.router.navigateByUrl('/analytics/review');
    }

    getPreferredSequence() {
        if (!this.currentLocation) {
            return;
        }
        const userType = this.currentUser?.type;
        this.settingService.getAllProperties(this.currentLocation.locationId, PropertyGroup.GENERAL).subscribe(properties => {
            const preferredSequence = properties.find(property => property.propertyKey === 'preferredSequence');
            if (preferredSequence) {
                localStorage.setItem('preferredSequence', preferredSequence.propertyValue);
                return;
            }
            if (userType === SetupType.GLOBAL || userType === SetupType.SHARED) {
                this.settingService.getAllProperties('', PropertyGroup.GENERAL).subscribe(orgProperties => {
                    const orgPreferred = orgProperties.find(property => property.propertyKey === 'preferredSequence');
                    if (orgPreferred) {
                        localStorage.setItem('preferredSequence', orgPreferred.propertyValue);
                    }
                });
            }
        });
    }

    get enrollRoute(): string {
        return this.setupProgressStatus > 1 ? '/collect-review/invite-customer' : '/collect-review/sequence';
    }

    get enrollLabelKey(): string {
        return this.setupProgressStatus > 1 ? 'Enroll Customer' : 'View Sequence';
    }
}
